import {CommonModule} from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  NO_ERRORS_SCHEMA,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ButtonsModule} from '@progress/kendo-angular-buttons';
import {
  DialogModule,
  DialogService,
  WindowCloseResult,
  WindowModule,
  WindowRef,
  WindowService
} from '@progress/kendo-angular-dialog';
import {GridModule, PagerPosition, PagerType} from '@progress/kendo-angular-grid';
import {InputsModule} from '@progress/kendo-angular-inputs';
import {LabelModule} from '@progress/kendo-angular-label';
import {LayoutModule} from '@progress/kendo-angular-layout';
import {PagerModule} from '@progress/kendo-angular-pager';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor} from '@progress/kendo-data-query';
import {BehaviorSubject, Subscription} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {faAddressCard, faEdit, faPlusCircle, faTrash, faTriangleExclamation} from "@fortawesome/free-solid-svg-icons";

import {all} from '@awesome.me/kit-787edf9aa0/icons';
import {MulticheckFilterComponent} from '../../filters/multicheck-filter/multicheck-filter.component';
import {DeleteConfirmComponent} from '../../common-components/delete-confirm/delete-confirm.component';
import {ErrorDialogModalComponent} from '../../common-components/error-dialog-modal/error-dialog-modal.component';
import {CasiProjectLabel, Project} from '../../models/models.lib';
import {defaultGridPageSize, FormModes} from '../../common/constants.lib';
import {LoggerService} from '../../shared/logger.service';
import {MeService} from '../../auth/me.service';
import {ProjectUserSelectionComponent} from '../project-user-selection/project-user-selection.component';
import {ProjectLabelService} from "../../tracker/project-label.service";
import {ProjectLabelAddComponent} from "../project-label-add/project-label-add.component";


@Component({
  selector: 'casi-project-labels',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    WindowModule,
    PagerModule,
    GridModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    LayoutModule,
    MulticheckFilterComponent,
    FontAwesomeModule,
    DeleteConfirmComponent,
    ErrorDialogModalComponent,
    ProjectUserSelectionComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './project-labels.component.html',
  styleUrl: './project-labels.component.scss'
})
export class ProjectLabelsComponent implements OnInit, OnDestroy {

  @Input({ required: true }) project: Project | undefined;
  @Input({ required: true }) labels: CasiProjectLabel[] | undefined;

  @Output() onClose = new EventEmitter<void>();

  public rawData: CasiProjectLabel[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: CasiProjectLabel[] = filterBy(this.rawData, this.filter);

  public currentItem: CasiProjectLabel | undefined;

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";
  public pageSize = defaultGridPageSize;

  public canUpdate: boolean = true;
  public canCreate: boolean = true;
  public canDestroy: boolean = true;
  public formMode: FormModes = FormModes.NOTSET;
  public addWinOpen: boolean = false;


  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  private _windowRef: WindowRef | undefined;

  private _subs: Subscription[] = [];

  constructor(
    private logger: LoggerService,
    public dataService: ProjectLabelService,
    private toastr: ToastrService,
    public faIcons: FaIconLibrary,
    private me: MeService,
    private dialogService: DialogService,
    private windowService: WindowService

  ) {
    faIcons.addIcons(faPlusCircle, faAddressCard, faEdit,
      faTrash, faTriangleExclamation);
    faIcons.addIcons(...all);
  }

  public sort: SortDescriptor[] = [
    {
      field: "project_label_description",
      dir: "asc",
    },
  ];

  //#region Ng Handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...', this.labels);

    this.dataService.query(this.project!.id);
    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:e: ', e);
      this.rawData = e;
      this.loadData();
    }))
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion


  //#region Grid handlers

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    // this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.rawData, fieldName).map((item: any) => item[fieldName]);
    // return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  //#endregion

  //#region view
  closeWindow(): void {
    this.logger.debug(`closeWindow...`);
    this.onClose.emit();
  }

  addLabel(titleBar: TemplateRef<unknown>): void {
    this.logger.debug(`addLabel...`);
    this.closeWindowRef();
    this.formMode = FormModes.NEW;
    this.showTitle = 'ADD LABEL';
    this._windowRef = this.windowService.open({
      titleBarContent: titleBar,
      content: ProjectLabelAddComponent,
      width: 600,
      minWidth: 320
    });
    this.currentItem = new CasiProjectLabel(this.project!.id)
    this.setFormInputs();

    this._windowRef.result.subscribe(results => {
      this.logger.debug('addLabel:results: ', results);
      if (results instanceof WindowCloseResult) {
        this.closeWindowRef();
      }
    });

    this._windowRef.content.instance.onCancel.subscribe(() => {
      this.logger.debug('addLabel:onClose');
      this.closeWindowRef();
    });

    this._windowRef.content.instance.onSave.subscribe((formData: FormGroup) => {
      this.logger.debug('addLabel:onSave', formData);
      this.saveProjectLabel(formData.value, this.formMode);
    });

    this.addWinOpen = true;
  }

  editLabel(dataItem: any, titleBar: TemplateRef<unknown>): void {
    this.logger.debug(`editLabel...`);
    this.closeWindowRef();
    this.formMode = FormModes.EDIT;
    this.showTitle = 'EDIT LABEL';
    this.currentItem = dataItem;
    this._windowRef = this.windowService.open({
      titleBarContent: titleBar,
      content: ProjectLabelAddComponent,
      width: 600,
      minWidth: 320
    });
    this.setFormInputs();

    this._windowRef.result.subscribe(results => {
      this.logger.debug('addLabel:results: ', results);
      if (results instanceof WindowCloseResult) {
        this.closeWindowRef();
      }
    });

    this._windowRef.content.instance.onCancel.subscribe(() => {
      this.logger.debug('addLabel:onClose');
      this.closeWindowRef();
    });

    this._windowRef.content.instance.onSave.subscribe((formData: FormGroup) => {
      this.logger.debug('addLabel:onSave', formData);
      this.saveProjectLabel(formData.value, this.formMode);
    });

    this.addWinOpen = true;
  }

  setActive(dataItem: any) {
    this.logger.debug('setActive:dataItem: ', dataItem);
    dataItem.active = !dataItem.active;
    this.dataService.update(dataItem)
      .then(() => {
        this.notify(dataItem.active ? 'Label Activated' : 'Label Deactivated')
        this.dataService.query(this.project!.id);
      })
      .catch(err => {
        this.logger.error('setActive:error: ', err);
        this.notify("Error Activated/Deactivated Label", true);
      })
  };

  //#endregion

  //#region Private functions

  private loadData(): void {
    this.logger.debug('loadData...');
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private closeWindowRef(): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
    this.addWinOpen = false;
  }

  private setFormInputs(): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.projectLabel = this.currentItem;
      this.logger.debug('setFormInputs: ', inputs);
    }
  }

  private saveProjectLabel(formData: any, mode: FormModes): void {
    this.logger.debug('saveProjectLabel...', formData);

    if(mode == FormModes.EDIT)
    this.dataService.update(formData)
      .then(resp => {
        this.notify('Label Updated');
        this.closeWindowRef();
        this.dataService.query(this.project!.id);
      })
      .catch(err => {
        this.logger.error('saveProjectLabel:', err);
        this.notify('Error saving user story', true);
      });
    else
      this.dataService.add(formData)
        .then(resp => {
          this.notify('Label Added');
          this.closeWindowRef();
          this.dataService.query(this.project!.id);
        })
        .catch(err => {
          this.logger.error('saveProjectLabel:', err);
          this.notify('Error saving user story', true);
        });
  }
  //#endregion

}

